import { useState, lazy, Suspense } from "react";
import PokemonFilter from "@/components/PokemonFilter";
import LoadingAnimation from "@/components/LoadingAnimation";
import { useLanguage } from "@/services/languageService";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
// We will need a way to get content from en-pokemon-randomizer-content.ts
// For now, let's assume StaticContent can be adapted or we'll create a new one.
import StaticContent from '@/components/StaticContent'; // Assuming it can be adapted
import { Pokemon, PokemonFilters, fetchRandomPokemon } from "@/services/pokeApi";
import { toast } from "sonner";
import MainLayout from "@/layouts/MainLayout";
import WhichPokemonBanner from "@/components/WhichPokemonBanner";

const PokemonGrid = lazy(() => import("@/components/PokemonGrid"));

const PokemonRandomizerPage = () => {
  const { t, currentLanguage } = useLanguage();
  const [pokemon, setPokemon] = useState<Pokemon[]>([]);
  const [loading, setLoading] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false); // New page might have this expanded by default or not

  const handleGenerate = async (count: number, filters: PokemonFilters) => {
    setLoading(true);
    try {
      const fetchedPokemon = await fetchRandomPokemon(count, filters);
      setPokemon([...fetchedPokemon]);
      if (fetchedPokemon.length > 0) {
        toast.success(`Generated ${fetchedPokemon.length} Pokémon using the Randomizer!`);
      }
      console.debug(`Current language (Randomizer Page): ${currentLanguage}, Fetched ${fetchedPokemon.length} Pokémon`);
    } catch (error) {
      console.error("Failed to generate Pokémon on Randomizer Page:", error);
      toast.error("Failed to generate Pokémon with the Randomizer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // New section data for the PokemonRandomizerPage
  const randomizerSections = [
    { id: "introduction", titleKey: "pokemonRandomizer_whatIsItTitle", contentSectionKey: "introduction" },
    { id: "features", titleKey: "pokemonRandomizer_featuresTitle", contentSectionKey: "features" },
    { id: "whyUse", titleKey: "pokemonRandomizer_whyUseTitle", contentSectionKey: "whyUse" },
    { id: "gettingStarted", titleKey: "pokemonRandomizer_gettingStartedTitle", contentSectionKey: "gettingStarted" },
    { id: "faq", titleKey: "pokemonRandomizer_faqTitle", contentSectionKey: "faq" },
  ];

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            {/* TODO: Add specific title for this page if different from the shared "title" key */}
            <h1 className="text-4xl font-bold text-pokemon-blue mb-2">{t("pokemonRandomizerPageH1")}</h1> 
            <p className="text-gray-600">{t("pokemonRandomizerPageSubtitle")}</p>
          </div>
          
          <div className="mb-8">
            <PokemonFilter onGenerate={handleGenerate} />
          </div>
          
          {loading ? (
            <LoadingAnimation className="my-12" />
          ) : (
            <Suspense fallback={<LoadingAnimation className="my-12" />}>
              <PokemonGrid pokemon={pokemon} />
            </Suspense>
          )}
          
          <WhichPokemonBanner /> {/* Reusing the banner */}
          
          {/* New SEO Content Section for Pokemon Randomizer Page */}
          <Suspense fallback={<div>Loading content...</div>}>
            <div className="mt-16 mb-12 bg-white rounded-lg shadow-md p-6">
              <Collapsible 
                open={isContentExpanded}
                onOpenChange={setIsContentExpanded}
                className="w-full"
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-left font-semibold text-lg text-pokemon-blue">
                  <h2>{t("pokemonRandomizerPageAboutTitle")}</h2>
                  {isContentExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4 text-gray-700 space-y-8">
                  {randomizerSections.map((section) => (
                    <section id={`randomizer-${section.id}`} key={section.id}>
                      <h3 className="text-2xl font-bold text-pokemon-blue mb-3">{t(section.titleKey)}</h3>
                      {/* This StaticContent call needs to be configured to pull from en-pokemon-randomizer-content.ts */}
                      <StaticContent section={section.contentSectionKey} contentType="pokemonRandomizer" />
                    </section>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            </div>
          </Suspense>
        </div>
      </div>
    </MainLayout>
  );
};

export default PokemonRandomizerPage; 